import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@roger/ui-components';
import { Check } from 'lucide-react';

import type { PayerEnrollmentGroup } from '../../services/clinicsApi';
import { agencyTitle, linesInView } from './payerEnrollments';

interface PayerDetailsDrawerProps {
  payer: PayerEnrollmentGroup | null;
  clinicIds: string[];
  onClose: () => void;
}

// Name, NOA, Prior auth: one grid for the header and every row, so they line up.
const GRID = 'grid grid-cols-[1fr_3.5rem_4.5rem] gap-2';
// A plain grey rule; the design token's opacity variants do not render here.
const RULE = 'border-t border-gray-200';

/** A ✓ when the insurance requires it, a dash when it does not. */
function Required({ value, label }: { value?: boolean | null; label: string }) {
  return (
    <span
      className='flex justify-center'
      aria-label={`${label} ${value ? 'required' : 'not required'}`}
      role='img'
    >
      {value ? (
        <Check className='h-3.5 w-3.5 text-green-600' strokeWidth={3} />
      ) : (
        <span className='text-muted-foreground/50'>—</span>
      )}
    </span>
  );
}

/**
 * A payer for reference, opened from its row's (i): the heading matches the
 * row, then the insurances behind it and its other names. The steps are
 * beside the table; this is for checking something specific.
 */
export function PayerDetailsDrawer({
  payer,
  clinicIds,
  onClose,
}: PayerDetailsDrawerProps) {
  const lines = payer ? linesInView(payer, clinicIds) : [];
  const title = payer ? agencyTitle(payer, clinicIds) : null;
  const otherNames = payer?.other_names ?? [];

  return (
    <Sheet open={payer !== null} onOpenChange={open => !open && onClose()}>
      <SheetContent className='w-full overflow-y-auto sm:max-w-lg'>
        {payer && (
          <>
            <SheetHeader>
              <SheetTitle title={title?.all.join(', ')}>
                {title?.title ?? payer.name}
              </SheetTitle>
              <SheetDescription>
                {title ? `Paid by ${payer.name} · ` : ''}Payer ID{' '}
                {payer.primary_payer_id ?? '—'}
              </SheetDescription>
            </SheetHeader>

            {lines.length > 0 && (
              <section className='mt-6'>
                {/* One quiet list, a rule between clinics. Only the
                    insurance names are in full-strength text; headings,
                    clinics and keys step back so the names lead. */}
                <div
                  className={`${GRID} items-end pb-1.5 text-xs text-muted-foreground`}
                >
                  <span className='text-sm font-semibold text-foreground'>
                    Insurances
                  </span>
                  <span className='text-center'>NOA</span>
                  <span className='text-center'>Prior auth</span>
                </div>
                {lines.map((line, index) => {
                  const insurances = line.insurances ?? [];
                  return (
                    <div
                      key={line.agency_clinic_id}
                      className={`pb-1.5 ${index > 0 ? RULE : ''}`}
                    >
                      <div className={`pt-2 text-xs text-muted-foreground`}>
                        {line.clinic_name ?? 'Unnamed clinic'}
                      </div>
                      {insurances.length === 0 ? (
                        <p className='py-0.5 pl-3 text-sm text-muted-foreground'>
                          None at this clinic.
                        </p>
                      ) : (
                        insurances.map(insurance => (
                          <div
                            key={insurance.id}
                            className={`${GRID} items-center py-0.5 pl-3`}
                          >
                            <div className='min-w-0 truncate text-sm'>
                              {insurance.name}
                              {insurance.insurance_key && (
                                <span className='ml-3 text-xs tabular-nums text-muted-foreground/70'>
                                  {insurance.insurance_key}
                                </span>
                              )}
                            </div>
                            <Required
                              value={insurance.noa_required}
                              label='NOA'
                            />
                            <Required
                              value={insurance.prior_auth_required}
                              label='Prior auth'
                            />
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </section>
            )}

            {otherNames.length > 0 && (
              <section className='mt-8 space-y-2'>
                <h3 className='text-sm font-medium'>Also known as</h3>
                <ul className='list-disc space-y-0.5 pl-5 text-sm text-muted-foreground'>
                  {otherNames.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
